import { useState, useEffect } from 'react';
import { Mail, Trash2, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactsAdmin = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchContacts(); }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (err) {
      console.error('Failed to fetch contacts', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/contacts/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (err) {
      alert('Failed to mark as read');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact inquiry?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (err) {
      alert('Failed to delete contact');
    }
  };

  const filteredContacts = filter === 'all' ? contacts : 
    filter === 'unread' ? contacts.filter(c => !c.read) : 
    contacts.filter(c => c.read);

  const unreadCount = contacts.filter(c => !c.read).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="contacts-admin-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase">Contact Inquiries</h1>
            <p className="text-slate-500 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All messages read'}
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'all', label: `All (${contacts.length})` },
            { key: 'unread', label: `Unread (${unreadCount})` },
            { key: 'read', label: `Read (${contacts.length - unreadCount})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-sm font-medium ${
                filter === f.key ? 'bg-orange-500 text-white' : 'bg-white border-2 border-slate-100 text-slate-600 hover:border-orange-500'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Contacts List */}
        <div className="space-y-3">
          {filteredContacts.map((contact) => (
            <div 
              key={contact.id} 
              className={`bg-white border-2 ${contact.read ? 'border-slate-100' : 'border-orange-200'}`}
              data-testid={`contact-item-${contact.id}`}
            >
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50"
                onClick={() => {
                  setExpandedId(expandedId === contact.id ? null : contact.id);
                  if (!contact.read) markAsRead(contact.id);
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900">
                        {contact.firstName} {contact.lastName}
                      </p>
                      {!contact.read && (
                        <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">New</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{contact.email}</p>
                    {contact.boatType && (
                      <p className="text-xs text-orange-500 mt-1">{contact.boatType}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-slate-400">
                      {new Date(contact.timestamp).toLocaleDateString()} {new Date(contact.timestamp).toLocaleTimeString()}
                    </p>
                    {expandedId === contact.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
              </div>

              {expandedId === contact.id && (
                <div className="border-t border-slate-100 p-4 bg-slate-50">
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Message</p>
                      <p className="text-slate-700 whitespace-pre-wrap">{contact.message}</p>
                    </div>
                    
                    {contact.phone && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Phone</p>
                        <p className="text-slate-700">{contact.phone}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
                      <a 
                        href={`mailto:${contact.email}?subject=Re: Armageddon Boats Inquiry`}
                        className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        Reply
                      </a>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(contact.id); }}
                        className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredContacts.length === 0 && (
          <div className="bg-white border-2 border-slate-100 p-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No contact inquiries yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ContactsAdmin;
